import React, { useEffect } from "react";
import { Button, Form, FormProps, Input, message, notification } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks.ts";
import {
  onChangePassword,
  onLogout,
} from "../../../redux/actions/authActions.tsx";
import { validatePassword } from "../../../utils/auth/commonValidators.ts";
import { useIsMobile } from "../../../hooks/useIsMobile.ts";
import { extractErrorMessage } from "../../../utils/extractErrorMessage.ts";

type FormValues = {
  current_password: string;
  new_password: string;
  confirm_new_password: string;
};

const ChangePasswordImage: React.FC = () => (
  <img
    src="/change-password-image.jpg"
    className="max-md:w-4/5 mx-auto block object-cover"
    alt="Login image"
    width={0}
    height={0}
    sizes="30vw"
    style={{ width: "100%" }}
  />
);

export const ChangePasswordForm: React.FC = () => {
  const [form] = Form.useForm<FormValues>();
  const { isMobile } = useIsMobile();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { loading, userInfo } = useAppSelector((state) => state.auth);

  const [messageApi, msgContextHolder] = message.useMessage();
  const [notifyApi, notifyContextHolder] = notification.useNotification();

  const handleNotifyUser = () => {
    if (userInfo?.is_validated) return;

    notifyApi["info"]({
      message: "Cambio de contraseña",
      description:
        "Necesitas cambiar tu contraseña para utilizar Multiexpediente.",
      placement: "top",
    });
  };

  useEffect(() => {
    handleNotifyUser();
  }, [location.pathname]);

  const onChangePasswordForm: FormProps<FormValues>["onFinish"] = async (
    changePasswordData,
  ) => {
    try {
      await dispatch(onChangePassword(changePasswordData)).unwrap();
      let message = "Contraseña cambiada correctamente.";

      if (!userInfo?.is_validated) {
        message +=
          "\nEn breve serás redirigido a la página de inicio de sesión.";
      }

      messageApi.success(message);
      form.resetFields();

      if (!userInfo?.is_validated) {
        setTimeout(async () => {
          await dispatch(onLogout());
          navigate("/auth/logout");
        }, 2000);
      }
    } catch (error: any) {
      messageApi.error(
        extractErrorMessage(error, "No se pudo actualizar la contraseña."),
      );
    }
  };

  return (
    <div className="font-[sans-serif] bg-white">
      {msgContextHolder}
      {notifyContextHolder}
      <div className="min-h-screen flex flex-col items-center justify-center py-6 px-4">
        <div className="grid md:grid-cols-2 items-center gap-4 max-w-6xl w-full">
          <div className="border border-gray-300 rounded-lg p-6 max-w-md shadow-[0_2px_22px_-4px_rgba(93,96,127,0.2)] max-md:mx-auto">
            <Form
              form={form}
              className="space-y-4"
              onFinish={onChangePasswordForm}
            >
              <div className="mb-8 mx-auto text-center">
                <h6 className="text-gray-800 text-2xl font-extrabold text-center">
                  Cambiar contraseña
                </h6>
              </div>

              {isMobile && <ChangePasswordImage />}

              <div className="flex flex-col">
                <Form.Item<FormValues>
                  label="Contraseña actual"
                  labelCol={{ span: 24 }}
                  name="current_password"
                  rules={[
                    {
                      required: true,
                      message: "Campo requerido.",
                    },
                  ]}
                >
                  <Input.Password />
                </Form.Item>

                <Form.Item<FormValues>
                  label="Nueva contraseña"
                  labelCol={{ span: 24 }}
                  name="new_password"
                  rules={[
                    {
                      required: true,
                      message: "Campo requerido.",
                    },
                    { validator: validatePassword },
                  ]}
                >
                  <Input.Password />
                </Form.Item>

                <Form.Item<FormValues>
                  label="Confirmar nueva contraseña"
                  labelCol={{ span: 24 }}
                  name="confirm_new_password"
                  rules={[
                    {
                      required: true,
                      message: "Campo requerido.",
                    },
                    {
                      validator: async (_, value) => {
                        if (value !== form.getFieldValue("new_password"))
                          return Promise.reject(
                            "Las contraseñas no coinciden.",
                          );
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input.Password />
                </Form.Item>
              </div>

              <div className="!mt-8">
                <Button
                  type="primary"
                  loading={loading}
                  htmlType="submit"
                  className="w-full shadow-xl py-6 px-7 text-sm tracking-wide rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none"
                >
                  Cambiar contraseña
                </Button>
              </div>
            </Form>
          </div>
          <div className="lg:h-[400px] md:h-[300px] max-md:mt-8 flex justify-center items-center">
            {!isMobile && <ChangePasswordImage />}
          </div>
        </div>
      </div>
    </div>
  );
};
